import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useUpdateSearchParamUrl } from 'hooks/hook';
import { isNil, omitBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { usePaging } from 'hooks/usePaging';
import { getFloorPlanFilters, getFloorPlans } from 'services/locations';
import { child, dropDownFilters, locations, floorPlanData } from '../../../types/floorplan';
import { SelectOptions } from 'app/components/elements/InputSelect';

export const HEADER_CLIENTS = [
  { label: 'Client Name', key: 'clientName', sort: 'clientName', width: 150 },
  { label: 'Location Path', key: 'locationpath', sort: 'locationpath', width: 250 },
  { label: 'Plan / Drawing', key: 'floorplan', sort: 'floorplan', width: 150 },
  { label: 'Open Deficiencies', key: 'deficiencies', sort: 'deficiencies', width: 150 },
  { label: 'Status', key: 'status', sort: 'status', width: 150 },
  { label: 'Last Updated', key: 'lastupdated', sort: 'lastupdated', width: 150 },
  { label: 'Actions', key: 'actions', width: 300 }
];

export const useInjection = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useUpdateSearchParamUrl();
  const [filterParams, setfilterParams] = useUpdateSearchParamUrl();
  const [dropdownData, setdropdownData] = useState<dropDownFilters>();
  const [sortBy, setSortBy] = useState('');
  const [order, setOrder] = useState<'asc' | 'desc'>();
  const [page, setPage] = useState<number>(1);
  const [pagesize, setPagesize] = useState<number>(10);
  const [floorplandata, setfloorplandata] = useState<floorPlanData>();
  const { pagingInfo, setCurrentPage, setTotal } = usePaging();
  const sortOption = useMemo(
    () => ({
      field: sortBy,
      order
    }),
    [sortBy, order]
  );

  const handleGetfloorplans = useCallback(async () => {
    const params = {
      size: pagesize,
      page: page,
      clientId: Number(filterParams.get('clientId')) || undefined,
      floorId: Number(filterParams.get('floorId')) || undefined,
      locationId: Number(filterParams.get('locationId')) || undefined
      // type: filterParams.get('type') || undefined,
    };
    const data = await getFloorPlans(params);
    setTotal(data.total);
    setfloorplandata(data);
  }, [page, filterParams]);

  const { asyncCallback: asyncGetInitfloorplans } = useAsyncCallback(handleGetfloorplans, []);

  useEffect(() => {
    asyncGetInitfloorplans();
  }, [asyncGetInitfloorplans]);

  const handleFilterChange = useCallback(
    (key: string, value: string) => {
      const params = Object.fromEntries(filterParams.entries());
      setfilterParams(omitBy({ ...params, [key]: value }, (v) => v == null || v === ''));
    },
    [filterParams, setfilterParams]
  );

  const filtersData = useCallback(async () => {
    const data = await getFloorPlanFilters();
    setdropdownData(data.data);
  }, []);

  const { asyncCallback: asyncFilterData } = useAsyncCallback(filtersData, []);
  useEffect(() => {
    asyncFilterData();
  }, [asyncFilterData]);

  const handleSearch = useCallback((searchText: string) => {
    setSearchParams(omitBy({ searchText }, isNil) as { [key: string]: string });
  }, []);

  const handleSort = useCallback(
    (field: string) => {
      if (field === sortBy) {
        setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(field);
        setOrder('asc');
      }
    },
    [sortBy]
  );

  const handleChangePage = useCallback(
    (page: number) => {
      setPage(page);
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  const clearFilters = useCallback(() => {
    const params = Object.fromEntries(filterParams.entries());

    delete params.clientId;
    delete params.floorId;
    delete params.locationId;

    setfilterParams(params);
  }, [filterParams, setfilterParams]);

  const onClickAction = useCallback(
    (href: string) => {
      navigate(href);
    },
    [navigate]
  );

  const handleuploadplan = useCallback(() => {
    navigate('/pdfmapping/uploadplan');
  }, [navigate]);

  const getLocationOptions = (locations: locations[]): SelectOptions[] => {
    const options: SelectOptions[] = [];

    const traverse = (nodes: locations[] | child[]) => {
      nodes.forEach((node) => {
        options.push({
          value: String(node.id),
          label: node.name
        });

        if (node.children?.length) {
          traverse(node.children);
        }
      });
    };
    traverse(locations);

    return options;
  };

  const clientOptions: SelectOptions[] = [
    {
      label: 'All Clients',
      value: ''
    },
    ...(dropdownData?.clients?.map((client) => ({
      label: client.name,
      value: String(client.id)
    })) ?? [])
  ];

  const locationOptions: SelectOptions[] = [
    {
      label: 'All Locations',
      value: ''
    },
    ...(dropdownData ? getLocationOptions(dropdownData.locations) : [])
  ];

  const floorOptions: SelectOptions[] = [
    {
      label: 'All Floors',
      value: ''
    },
    ...(dropdownData?.floors?.map((floor) => ({
      label: floor.name,
      value: String(floor.id)
    })) ?? [])
  ];

  const roomOptions: SelectOptions[] = [
    {
      label: 'All Floors',
      value: ''
    },
    ...(dropdownData?.rooms?.map((room) => ({
      label: room.name,
      value: String(room.id)
    })) ?? [])
  ];
  const statusOptions: SelectOptions[] = [
    {
      label: 'All Status',
      value: ''
    }
  ];

  return {
    handleSearch,
    handleChangePage,
    handleFilterChange,
    clientOptions,
    floorOptions,
    locationOptions,
    setPagesize,
    sortOption,
    statusOptions,
    floorplandata,
    pagingInfo,
    filterParams,
    handleSort,
    handleuploadplan,
    searchParams,
    clearFilters,
    onClickAction,
    roomOptions
  };
};
